import Header from '@components/containers/header';
import FaviconHead from '@components/head/favicons';
import AppLayout from '@components/layouts/appLayout';
import SearchBarLogic from '@components/search/Bar/searchBarLogic';
import CustomTextAnswerHits from '@components/search/Hits/CustomTextAnswerHits';
import {
    COMMON,
    HOME,
    ROUTE_PRODUCT,
    SEARCH,
    SECONDS_PER_DAY,
} from '@constants';
import { getIndexesName, searchClient } from '@lib/algolia';
import { getCompanyConfig } from '@lib/uikit';
import { PageWithProduct } from '@types';
import {
    withApolloClient,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodsPlugins';
import capitalize from '@utils/string/capitalize';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Configure, Index, InstantSearch } from 'react-instantsearch-dom';

const CustomStoryHits = dynamic(
    () => import('@components/search/Hits/CustomStoryHits')
);
const CustomStateResults = dynamic(
    () => import('@components/search/StateResults')
);

interface Props extends PageWithProduct {}

const SearchPage: NextPage<Props> = ({ productMeta }: Props) => {
    const { t } = useTranslation(COMMON);
    const { query, locale } = useRouter();
    const { company, product, searchQuery } = query;

    if (typeof product !== 'string') {
        throw Error(
            'Received invalid product slug in /[company]/[product]/search'
        );
    }

    if (typeof company !== 'string') {
        throw Error(
            'Received invalid company slug in /[company]/[product]/search'
        );
    }

    const indexes = getIndexesName();

    return (
        <>
            <Head>
                <title>Search</title>
                <meta name='robots' content='noindex' />

                {/* Prevent zoom after input focus on Mozilla */}
                <meta
                    name='viewport'
                    content='width=device-width, height=device-height, initial-scale=1, minimum-scale=1, maximum-scale=1'
                />
            </Head>
            {/* Still using company favicon because product is not fetch in the page */}
            <FaviconHead productMeta={productMeta} />
            <AppLayout
                header={
                    <Header
                        left={{
                            display: 'back',
                            backHref: { pathname: ROUTE_PRODUCT, query },
                            backAs: `/${product}`,
                        }}
                        center={{
                            display: 'text',
                            text: { title: capitalize(t('search')) },
                        }}
                    />
                }
                body={
                    <InstantSearch
                        indexName={indexes.stories}
                        searchClient={searchClient}
                    >
                        <Configure
                            filters={`locale:${locale} AND Products.Slug:${product} AND CompanySlug:${company}`}
                            clickAnalytics
                            analyticsTags={[
                                `locale:${locale}`,
                                `company:${company}`,
                                `product:${product}`,
                            ]}
                        />
                        <SearchBarLogic searchQuery={searchQuery} />
                        <Index indexName={indexes.stories}>
                            <CustomStoryHits />
                        </Index>
                        <Index indexName={indexes.textAnswers}>
                            <Configure hitsPerPage={5} />
                            <CustomTextAnswerHits />
                        </Index>
                        {/* Handles state results */}
                        <CustomStateResults />
                    </InstantSearch>
                }
            />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps = withErrorsGSP<Props>(
    withApolloClient<Props>(
        withCompany<Props>(
            withProduct<Props>(
                async ({ apolloClient, company, locale, defaultLocale }) => {
                    return {
                        props: {
                            ...(await serverSideTranslations(
                                locale || defaultLocale!,
                                [COMMON, SEARCH, HOME]
                            )),
                            ...(await getCompanyConfig(
                                apolloClient,
                                company.Slug!,
                                locale!
                            )),
                        },
                        revalidate: SECONDS_PER_DAY,
                    };
                }
            )
        )
    )
);

export default SearchPage;
